import {
  AfterViewInit,
  Component, ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges, ViewChild
} from '@angular/core';
import DoingModel from '../../../../model/doing.model';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import NoteModel from "../../../../model/note.model";
import {BehaviorSubject, combineLatest, forkJoin, of, Subject} from "rxjs";
import {debounceTime, distinctUntilChanged, switchMap} from "rxjs/operators";
import * as moment from "moment/moment";
import CooperationService from "../../../../api/cooperation/cooperation.service";
import {MatDialog} from "@angular/material/dialog";
import UserService from "../../../../api/user/user.service";
import CustomerService from "../../../../api/customer/customer.service";
import {CustomerFormComponent, CustomerFormDialogModel} from "../../customer/customer-form/customer-form.component";
import CustomerModel from "../../../../model/customer.model";
import {MatSnackBar} from "@angular/material/snack-bar";
import {MatMenuTrigger} from "@angular/material/menu";

@Component({
  selector: 'app-doing-form',
  templateUrl: './doing-form.component.html',
  styleUrls: ['./doing-form.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DoingFormComponent),
    multi: true
  }]
})
export class DoingFormComponent implements OnInit, AfterViewInit, ControlValueAccessor, OnChanges {
  public doing: DoingModel;
  public disabled = false;
  public searchOpened = false;

  private positionSubject = new BehaviorSubject<'top'|'bottom'>('bottom');
  public position$ = this.positionSubject.asObservable();

  private querySubject = new BehaviorSubject<string>('');
  public query$ = this.querySubject.asObservable();


  private resultsSubject = new BehaviorSubject<QueryResult[]>([]);
  public results$ = this.resultsSubject.asObservable();

  private focusSubject = new Subject();
  public focus$ = this.focusSubject.asObservable();

  public selectedIndex = 0;

  @Input() cooperationSearchEnabled = true;
  @Input() userSearchEnabled = true;
  @Input() customerSearchEnabled = false;

  @Input() autofocus = false;

  @Input() tagTrigger = '#';
  @Input() placeholderText = 'Doing hinzufügen';
  @Input() defaultFollowUpDate = null;

  @Output() doingSubmitted = new EventEmitter<DoingModel>();

  @ViewChild('doingInput') doingInput: ElementRef;
  @ViewChild('searchQueryInput') searchQueryInput: ElementRef;
  @ViewChild('searchMenuTrigger', { read: ElementRef }) searchMenuTrigger: ElementRef;

  private onChange: any = () => { };
  private onTouched: any = () => { };

  constructor(private cooperationService: CooperationService,
              private dialog: MatDialog,
              private userService: UserService,
              private el: ElementRef,
              private snackBar: MatSnackBar,
              private customerService: CustomerService) {

  }

  writeValue(obj: any): void {
    if(obj) {
      this.doing = obj;
    } else {
      this.doing = {
        text: '',
        state: 'new'
      };
    }
  }

  saveDoing() {
    this.doingSubmitted.emit(this.doing)
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnInit(): void {
    if(!this.doing) {
      this.doing = {
        text: '',
        state: 'new',
        followUp: this.defaultFollowUpDate
      };
      console.log(this.doing);
    }

    this.focus$.pipe(
        debounceTime(200),
        distinctUntilChanged()
    ).subscribe(focus => {
      if (!focus) {
        this.focusLost();
      }
    });

    this.query$.pipe(
        debounceTime(200),
        distinctUntilChanged()
    ).subscribe(query => {
      if (!this.searchOpened) { return; }
      const requests = [];

      if (this.userSearchEnabled) {
        requests.push(this.userService.list(query, 1, 3)
            .pipe(switchMap(result => {
              const resultList = [] as QueryResult[];
              for (const user of result.results) {
                resultList.push({
                  id: user.id,
                  originalObject: user,
                  label: user.displayName ? user.displayName : user.username,
                  type: 'user',
                });
              }
              return of(resultList);
            })));
      }

      if (this.customerSearchEnabled) {
        requests.push(this.customerService.list(query, 1, 15, null, null, { state: 'customer' })
            .pipe(switchMap(result => {
              const resultList = [] as QueryResult[];
              for (const customer of result.results) {
                resultList.push({
                  id: customer.id,
                  label: customer.companyDisplayName ? customer.companyDisplayName : customer.companyName,
                  type: 'customer',
                  originalObject: customer,
                  image: customer.logo ? customer.logo.filename : null
                });
              }
              return of(resultList);
            })));
      }

      if (this.cooperationSearchEnabled) {
        let cooperationAdditionalParameters = {};
        if(this.doing.artist) {
          cooperationAdditionalParameters = {
            artist: this.doing.artist.id
          };
        }
        requests.push(
            this.cooperationService.list(query, 1, 15, 'created', 'desc',  cooperationAdditionalParameters)
                .pipe(switchMap(result => {
                  const resultList = [] as QueryResult[];
                  for (const coop of result.results) {
                    const dateframe = [];
                    if(coop.earliestEvent) {
                      dateframe.push(moment(coop.earliestEvent).format('DD.MM.YYYY'));
                    }
                    if(coop.latestEvent) {
                      dateframe.push(moment(coop.latestEvent).format('DD.MM.YYYY'));
                    }
                    resultList.push({
                      id: coop.id,
                      originalObject: coop,
                      label: coop.name,
                      image: coop.customer.logo ? coop.customer.logo.filename : null,
                      type: 'cooperation',
                      description: dateframe.join(' - ')
                    });
                  }
                  return of(resultList);
                }))
        );
      }

      combineLatest(requests).subscribe(results => {
        let combinedResults = [];
        for (const result of results) {
          combinedResults = combinedResults.concat(result);
        }
        this.resultsSubject.next(combinedResults);
      });
    });
  }

  keyDown(event) {
    event.stopPropagation();
    console.log(event.key);
    if (event.key === this.tagTrigger && !this.searchOpened ) {
      event.preventDefault();
      this.searchOpened = true;
      this.searchMenuTrigger.nativeElement.click();
      this.querySubject.next('');
      this.selectedIndex = 0;
      setTimeout(() => {
        this.searchQueryInput.nativeElement.focus();
      }, 10);
    }

    if (event.key === 'Enter' && this.doingInput.nativeElement.value != '') {
      this.doingSubmitted.emit(this.doing);
      this.doing = {
        text: '',
        user: null,
        customer: null,
        markedUser: null,
        cooperation: null,
        state: 'new'
      };
      this.doingInput.nativeElement.value = '';
    }
  }

  ngOnChanges(changes: SimpleChanges) {

  }

  ngAfterViewInit() {
    this.results$.subscribe(results => {
      if(this.positionSubject.getValue() == 'top') {
        this.selectedIndex = results.length - 1;
      } else {
        this.selectedIndex = 0;
      }
    });
    window.setInterval(() => {
      let rect = this.el.nativeElement.getBoundingClientRect();
      let remainingSpaceBelow = window.innerHeight - rect.top - rect.height;
      if(remainingSpaceBelow < 300) {
        this.positionSubject.next('top');
      } else {
        this.positionSubject.next('bottom');
      }
    }, 2000);

    if(this.autofocus) {
      setTimeout(() => {
        this.doingInput.nativeElement.focus();
      }, 500);
    }
  }

  focusLost() {
    this.searchOpened = false;
  }

  backspacePressed() {
    if(this.querySubject.getValue() == '') {
      this.searchOpened = false;
      this.searchMenuTrigger.nativeElement.click();
      setTimeout(() => {
        this.doingInput.nativeElement.focus();
      }, 10);
    }
  }

  escapePressed() {
    this.searchOpened = false;
    this.searchMenuTrigger.nativeElement.click();
    setTimeout(() => {
      this.doingInput.nativeElement.focus();
    }, 10);
  }

  arrowDownPressed() {
    if(this.positionSubject.getValue() == 'top') {
      if (this.selectedIndex  < this.resultsSubject.getValue().length - 1) {
        this.selectedIndex++;
      }
    } else {
      if (this.selectedIndex > 0) {
        this.selectedIndex--;
      }
    }
  }

  arrowUpPressed() {
    if(this.positionSubject.getValue() == 'top') {
      if (this.selectedIndex > 0) {
        this.selectedIndex--;
      }
    } else {
      if (this.selectedIndex  < this.resultsSubject.getValue().length - 1) {
        this.selectedIndex++;
      }
    }
  }

  matchInType(type: string) {
    for(let result of this.resultsSubject.getValue()) {
      if(result.type == type) {
        return true;
      }
    }
    return false;
  }

  setQuery(query) {
    this.querySubject.next(query);
  }

  addCustomer() {
    const dialogData = new CustomerFormDialogModel({
      companyName: this.querySubject.getValue(),
      companyDisplayName: this.querySubject.getValue(),
      socialMedia: [],
    } as CustomerModel);

    const dialogRef = this.dialog.open(CustomerFormComponent, {
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        this.customerService.create(dialogResult).subscribe(result => {
          this.snackBar.open('Unternehmen wurde hinzugefügt');
          this.doing.customer = result;
          this.searchOpened = false;
        }, error => {
          this.snackBar.open('Fehler beim Hinzufügen des Unternehmens');
        });
      }
    });
  }

  saveSearch() {
    if (this.searchOpened) {
      const entries = this.resultsSubject.getValue();
      if (0 <= this.selectedIndex && this.selectedIndex < entries.length) {
        const entry = entries[this.selectedIndex];
        if (entry.type === 'customer') {
          this.doing.customer = entry.originalObject;
        } else if (entry.type === 'user') {
          this.doing.markedUser = entry.originalObject;
        } else if (entry.type === 'cooperation') {
          this.doing.cooperation = entry.originalObject;
        }
      }
    }
    this.searchOpened = false;
  }

  public focus() {
    this.doingInput.nativeElement.focus();
  }

  menuClosed() {
    this.searchOpened = false;
    this.searchQueryInput.nativeElement.value = '';
    setTimeout(() => {
      this.focus();
    }, 20)
  }

  applyContext(context: QueryResult) {
    console.log('apply context', context);
    if(context.type == 'customer') {
      this.doing.customer = context.originalObject;
    } else if(context.type == 'user') {
      this.doing.markedUser = context.originalObject;
    } else if(context.type == 'cooperation') {
      this.doing.cooperation = context.originalObject;
    }
  }

}
interface QueryResult {
  id: number;
  type: 'customer'|'cooperation'|'user';
  label: string;
  description?: string;
  image?: string;
  originalObject: any;
}
